import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { ErrorBoundary } from 'components/error-boundary/ErrorBoundary';
import { Loader } from 'components/loader/loader';
import { AppRoutes } from 'config/routes';
import { useHasApplicationParticipation, useLimits } from 'hooks';
import { ASSETS_APPLICATION_ALIAS } from 'modules/dashboard/assets/constants';
import s from './DashboardPage.module.scss';

const Infopanel = lazy(() => import('../infopanel/Infopanel'));
const Counterparties = lazy(() => import('../counterparties/Counterparties'));
const CounterpatyDetail = lazy(() =>
  import('../counterparties/counterparty-detail/CounterpatyDetail').then((m) => ({
    default: m.CounterpatyDetail,
  })),
);
const InvitationContent = lazy(() =>
  import('../invitation').then((m) => ({ default: m.InvitationContent })),
);
const Contracts = lazy(() => import('../contracts'));
const PowersAttorney = lazy(() => import('../powers-attorney/PowersAttorney'));
const Profile = lazy(() => import('../profile/Profile').then((m) => ({ default: m.Profile })));
const OrganizationProfile = lazy(() =>
  import('../organization-profile/OrganizationProfile').then((m) => ({
    default: m.OrganizationProfile,
  })),
);
const Organizations = lazy(() => import('../organization/Organization'));
const Staff = lazy(() => import('../staff/Staff').then((m) => ({ default: m.Staff })));
const CustomWidget = lazy(() => import('../custom-widget'));
const Employees = lazy(() => import('../employee-management'));
const OperationalRequests = lazy(() => import('../fuel-supply/operational-requests'));
const Assets = lazy(() => import('../assets'));
const ShipmentTasks = lazy(() => import('../fuel-supply/shipment-tasks'));
const ControlCards = lazy(() => import('../fuel-supply/control-cards'));
const ShipmentReports = lazy(() => import('../fuel-supply/shipment-reports'));
const NewShipmentRegistry = lazy(() => import('../fuel-supply/shipment-reports/new/New'));
const ShipmentRegistry = lazy(() => import('../fuel-supply/shipment-registry'));
const Settings = lazy(() => import('../settings/Settings'));

function AssetsAccessGate({ children }: { children: ReactNode }) {
  const { allowed, isLoading } = useHasApplicationParticipation(ASSETS_APPLICATION_ALIAS);

  if (isLoading) {
    return <Loader size="big" />;
  }

  if (!allowed) {
    return <Navigate to={AppRoutes.Dashboard.Contracts.Home} replace />;
  }

  return <>{children}</>;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { isLoading, isLimitedUser } = useLimits();

  const {
    Profile: ProfileRoute,
    Settings: SettingsRoute,
    Contracts: ContractsRoute,
    Infopanel: InfopanelRoute,
    Organizations: OrganizationRoute,
    ShipmentTasks: ShipmentTasksRoute,
    ShipmentReports: ShipmentReportsRoute,
    ControlCards: ControlCardsRoute,
    Counterparties: CounterpartiesRoute,
    PowersAttorney: PowersAttorneyRoute,
    OrganizationProfile: OrganizationProfileRoute,
    OperationalRequests: OperationalRequestsRoute,
    Assets: AssetsRoute,
    CustomWidget: CustomWidgetRoute,
    ShipmentRegistry: ShipmentRegistryRoute,
    Employees: EmployeesRoute,
  } = AppRoutes.Dashboard;

  useEffect(() => {
    if (!isLoading && isLimitedUser) {
      if (!window.location.pathname.includes(PowersAttorneyRoute.Home)) {
        navigate(AppRoutes.Auth.Home + AppRoutes.Auth.UserRestricted);
      }
    }
  }, [PowersAttorneyRoute.Home, isLimitedUser, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className={s.container}>
        <Loader size="big" />
      </div>
    );
  }

  if (isLimitedUser) {
    return null;
  }

  return (
    <div className={s.container}>
      <ErrorBoundary>
        <Suspense fallback={<Loader size="big" />}>
          <Routes>
            <Route path={InfopanelRoute + '/*'} element={<Infopanel />} />
            <Route path={CounterpartiesRoute.Home + '/*'} element={<Counterparties />} />
            <Route
              path={
                CounterpartiesRoute.Home +
                CounterpartiesRoute.CounterpatyDetail.Home +
                '/:id' +
                '/*'
              }
              element={<CounterpatyDetail />}
            />
            <Route
              path={CounterpartiesRoute.Home + CounterpartiesRoute.Invitation.Home + '/*'}
              element={<InvitationContent />}
            />
            <Route path={ContractsRoute.Home + '/*'} element={<Contracts />} />
            <Route path={PowersAttorneyRoute.Home + '/*'} element={<PowersAttorney />} />
            <Route path={ProfileRoute.Home + '/*'} element={<Profile />} />
            <Route
              path={OrganizationProfileRoute.Home + '/:id'}
              element={<OrganizationProfile />}
            />
            <Route path={OrganizationRoute.Home + '/*'} element={<Organizations />} />
            <Route
              path={OrganizationRoute.Home + OrganizationRoute.Staff + '/*'}
              element={<Staff />}
            />
            <Route path={CustomWidgetRoute.Home + '/*'} element={<CustomWidget />} />
            <Route path={EmployeesRoute.Home + '/*'} element={<Employees />} />
            <Route
              path={OperationalRequestsRoute.Home + '/*'}
              element={<OperationalRequests />}
            />
            <Route
              path={AssetsRoute.Home + '/*'}
              element={
                <AssetsAccessGate>
                  <Assets />
                </AssetsAccessGate>
              }
            />
            <Route path={ShipmentTasksRoute.Home + '/*'} element={<ShipmentTasks />} />
            <Route path={ControlCardsRoute.Home + '/*'} element={<ControlCards />} />
            <Route path={ShipmentReportsRoute.Home + '/*'} element={<ShipmentReports />} />
            <Route path={ShipmentReportsRoute.New + '/*'} element={<NewShipmentRegistry />} />
            <Route path={ShipmentRegistryRoute.Home + '/*'} element={<ShipmentRegistry />} />
            <Route
              path={SettingsRoute.Home + SettingsRoute.EDOOperator + '/*'}
              element={<Settings />}
            />
            {/* Неизвестный адрес внутри кабинета раньше показывал пустую страницу. */}
            <Route path="*" element={<Navigate to={ContractsRoute.Home} replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
